import { useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatNumber, setLine } from "./lib/format";
import { chartStart, lineChart, nearestPoint } from "./lib/chart";
import { bestPoint, liftWords, trendDay } from "./lib/progress";
import { useElementWidth } from "./lib/useElementWidth";

const HEIGHT = 176;
// Room for the y labels on the left ("190 lb"), the month labels below,
// and the best's label above the highest point.
const MARGIN = { top: 26, right: 16, bottom: 26, left: 46 };
const DOT = 4; // r: an 8 px marker
const REACH = 12; // the plot's tap target reaches this far past its edges

/**
 * One main lift's estimated max over the last 26 weeks (spec §6, D15):
 * the line in words first — the change since the first point, the best,
 * or what will appear — then the chart, which draws only what the words
 * and the table say. The x axis starts at the lift's first point, not the
 * window's start (chartStart). The SVG is aria-hidden and has ONE target:
 * a tap or click anywhere on the plot picks the nearest dot and says its
 * session under the chart, with a link to open it — per-dot targets were
 * ~6 px wide on a phone, and touch adjustment opened the neighbour. The
 * keyboard and screen-reader path, and the precise one, is *Show as a
 * table*: every point as a link to its session, with its estimate and the
 * set it came from. Whether it's open is the caller's (Progress keeps it in
 * the URL); a session opened from here carries `{from: "progress"}`, so its
 * page links back to Progress.
 */
function LiftChart({ lift, units, exercise, todayKey, tableOpen = false, onToggleTable, from = "" }) {
  const id = useId();
  const box = useRef(null);
  const svg = useRef(null);
  const [picked, setPicked] = useState(null); // a session id, from a tap on the plot
  const width = useElementWidth(box);
  // Where its sessions link back to: Progress, with the tables open now.
  const state = { from: "progress", progressSearch: from };
  const trend = useMemo(() => lift.trend ?? [], [lift.trend]);
  const best = bestPoint(lift);
  const withBody = exercise?.load_kind === "bodyweight";
  const chart = useMemo(
    () =>
      lineChart(trend, {
        start: chartStart(
          trend.map((point) => point.date),
          todayKey,
        ),
        end: todayKey,
        width,
        height: HEIGHT,
        margin: MARGIN,
      }),
    [trend, todayKey, width],
  );
  const placedBest = chart.points.find((placed) => placed.point === best) ?? null;
  const placedPick = chart.points.find((placed) => placed.point.session === picked) ?? null;
  const words = liftWords(lift, { units, exercise, todayKey });
  const pick = (event) => {
    const left = svg.current?.getBoundingClientRect().left ?? 0;
    const index = nearestPoint(chart.points, event.clientX - left);
    if (index !== -1) setPicked(chart.points[index].point.session);
  };

  return (
    <li aria-labelledby={`${id}-name`} className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <div>
        <h3 id={`${id}-name`} className="m-0 font-display text-lg font-semibold">
          {lift.name}
        </h3>
        <p className="mt-1 mb-0 text-sm tabular-nums">{words.join(" ")}</p>
      </div>

      {trend.length > 0 && (
        <>
          <div ref={box} aria-hidden="true" data-chart className="text-muted-foreground">
            <svg ref={svg} width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} className="block" fontSize={11}>
              {chart.yTicks.map((tick, i) => (
                <g key={`y${tick.value}`}>
                  <line
                    x1={chart.plot.left}
                    x2={chart.plot.right}
                    y1={tick.y}
                    y2={tick.y}
                    stroke="var(--border)"
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                  <text x={chart.plot.left - 6} y={tick.y} textAnchor="end" dominantBaseline="middle" fill="currentColor">
                    {formatNumber(tick.value)}
                    {i === chart.yTicks.length - 1 ? ` ${units}` : ""}
                  </text>
                </g>
              ))}
              {chart.xTicks.map((tick) => (
                <g key={`x${tick.day}`}>
                  <line
                    x1={tick.x}
                    x2={tick.x}
                    y1={chart.plot.bottom}
                    y2={chart.plot.bottom + 4}
                    stroke="var(--border)"
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                  <text x={tick.x} y={chart.plot.bottom + 17} textAnchor="middle" fill="currentColor">
                    {tick.label}
                  </text>
                </g>
              ))}
              {chart.path && (
                <path
                  d={chart.path}
                  fill="none"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )}
              {/* The one target: the plot and a little past it. A tap
                  anywhere picks the nearest dot (nearestPoint); the marks
                  drawn over it take no pointer. */}
              <rect
                data-chart-target
                x={chart.plot.left - REACH}
                y={chart.plot.top - REACH}
                width={chart.plot.right - chart.plot.left + 2 * REACH}
                height={chart.plot.bottom - chart.plot.top + 2 * REACH}
                fill="transparent"
                className="cursor-pointer"
                onClick={pick}
              />
              {placedBest && (
                <circle
                  data-best
                  cx={placedBest.x}
                  cy={placedBest.y}
                  r={DOT + 4}
                  fill="none"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  pointerEvents="none"
                />
              )}
              {placedPick && (
                <circle
                  data-picked={placedPick.point.session}
                  cx={placedPick.x}
                  cy={placedPick.y}
                  r={DOT + 2}
                  fill="var(--foreground)"
                  pointerEvents="none"
                />
              )}
              <g pointerEvents="none">
                {chart.points.map(({ x, y, point }) => (
                  <circle
                    key={point.session}
                    data-dot={point.session}
                    cx={x}
                    cy={y}
                    r={DOT}
                    fill="var(--primary)"
                    stroke="var(--card)"
                    strokeWidth={2}
                  />
                ))}
              </g>
              {placedBest && (
                <text
                  x={placedBest.x}
                  // above the ring: a point is never above the plot's top,
                  // and MARGIN.top leaves the label room there
                  y={placedBest.y - 14}
                  pointerEvents="none"
                  textAnchor={
                    placedBest.x > chart.plot.right - 24
                      ? "end"
                      : placedBest.x < chart.plot.left + 24
                        ? "start"
                        : "middle"
                  }
                  fill="var(--foreground)"
                  fontWeight={600}
                >
                  Best
                </text>
              )}
            </svg>
          </div>
          {placedPick ? (
            <p data-picked-line className="m-0 flex flex-wrap items-center gap-x-2 text-sm tabular-nums">
              <span>
                {trendDay(placedPick.point.date, todayKey)}: ~{formatNumber(Math.round(placedPick.point.e1rm))} {units}
                {placedPick.point.set ? ` from ${setLine(placedPick.point.set, units, exercise)}` : ""}
                {placedPick.point === best ? " — the best" : ""}.
              </span>{" "}
              <Link
                to={`/training/history/${placedPick.point.session}`}
                state={state}
                className="inline-flex min-h-11 items-center text-primary underline-offset-4 hover:underline md:min-h-8"
              >
                Open this session
              </Link>
            </p>
          ) : (
            <p aria-hidden="true" className="m-0 text-xs text-muted-foreground">
              A dot per session — its best set — and the best ringed. Tap or click near a dot to see its session; Show as a
              table lists them all.
            </p>
          )}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 md:h-8"
              data-table-toggle={lift.exercise}
              aria-expanded={tableOpen}
              aria-controls={`${id}-table`}
              onClick={() => onToggleTable?.(lift.exercise)}
            >
              <ChevronDown aria-hidden="true" className={cn("transition-transform", tableOpen && "rotate-180")} />
              {tableOpen ? "Hide the table" : "Show as a table"}{" "}
              <span className="sr-only">of {lift.name}</span>
            </Button>
          </div>
          <div id={`${id}-table`} hidden={!tableOpen} className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm tabular-nums">
              <caption className="sr-only">
                {lift.name}: estimated max per session, newest first
              </caption>
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th scope="col" className="py-1 pr-3 font-medium">
                    Session
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    Estimated max{withBody ? " (including bodyweight)" : ""}
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    From
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...trend].reverse().map((point) => (
                  <tr key={point.session} className="border-t align-baseline">
                    <td className="py-0.5 pr-3 whitespace-nowrap">
                      <Link
                        to={`/training/history/${point.session}`}
                        state={state}
                        className="inline-flex min-h-11 items-center text-primary underline-offset-4 hover:underline md:min-h-8"
                      >
                        {trendDay(point.date, todayKey)}
                      </Link>
                    </td>
                    <td className="py-0.5 pr-3 whitespace-nowrap">
                      {formatNumber(point.e1rm)} {units}
                      {point === best && (
                        <>
                          {" "}
                          <Badge variant="secondary">Best</Badge>
                        </>
                      )}
                    </td>
                    <td className="py-0.5">{point.set ? setLine(point.set, units, exercise) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </li>
  );
}

LiftChart.propTypes = {
  /** A TrainingLiftTrend: {exercise, name, trend: [{date, e1rm, session, set}], best, latest}. */
  lift: PropTypes.shape({
    exercise: PropTypes.string.isRequired,
    name: PropTypes.string,
    trend: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        e1rm: PropTypes.number.isRequired,
        session: PropTypes.number.isRequired,
        set: PropTypes.object,
      }),
    ),
    best: PropTypes.number,
    latest: PropTypes.number,
  }).isRequired,
  /** summary/'s units: every number here is in them. */
  units: PropTypes.string.isRequired,
  /** The catalog's entry (load kind: bodyweight lifts say so). */
  exercise: PropTypes.object,
  /** Today in the settings' zone ("YYYY-MM-DD"): the 26 weeks end here. */
  todayKey: PropTypes.string.isRequired,
  /** Whether *Show as a table* is open (Progress keeps it in its URL, so Back finds it open). */
  tableOpen: PropTypes.bool,
  /** Open or close the table: called with the lift's key. */
  onToggleTable: PropTypes.func,
  /** Progress's query, carried to a session so it can link back (with the tables open). */
  from: PropTypes.string,
};

export default LiftChart;
